/**
 * ResearchCitationPanel
 *
 * Side panel that displays citation details from research results.
 * Shows the exact section/paragraph content from retrieval data that
 * was used to support the synthesis answer. Allows lawyers to review
 * the source material directly.
 */

import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HiOutlineBookOpen,
  HiOutlineScale,
  HiOutlineDocumentText,
  HiOutlineMagnifyingGlass,
} from 'react-icons/hi2';
import { PiXLight } from 'react-icons/pi';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface RetrievalCitation {
  id?: string;
  title?: string;
  content?: string;
  score?: number;
  source?: string;
  relevance_score?: number;
  datasource_uuid?: string;
}

interface KeyAuthority {
  citation: string;
  principle: string;
  weight: string;
  application_notes?: string;
}

interface ResearchCitationPanelProps {
  /** The citation name/string clicked by the user (e.g. "Arbeidsmiljøloven § 14-9") */
  citationName: string;
  /** Full synthesis data from the message */
  synthesisData?: Record<string, any>;
  /** Retrieval data from blob: { source_key: { response, citations: [...] } } */
  retrievalData?: Record<string, any>;
  onClose: () => void;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

/**
 * Match a citation name (from synthesis) to retrieval results by title.
 * Uses normalized substring matching since LLM-generated names may differ
 * slightly from search index titles.
 */
function findMatchingRetrievalContent(
  citationName: string,
  retrievalData?: Record<string, any>
): RetrievalCitation[] {
  if (!retrievalData || !citationName) return [];

  const normalised = citationName.toLowerCase().replace(/\s+/g, ' ').trim();
  const matches: RetrievalCitation[] = [];

  for (const [, sourceData] of Object.entries(retrievalData)) {
    const citations: RetrievalCitation[] = sourceData?.citations || [];
    for (const cit of citations) {
      const title = (cit.title || '').toLowerCase().replace(/\s+/g, ' ').trim();
      if (!title) continue;

      // Exact match, contains match, or overlapping fragments
      if (
        title === normalised ||
        title.includes(normalised) ||
        normalised.includes(title) ||
        _fragmentOverlap(normalised, title)
      ) {
        matches.push(cit);
      }
    }
  }

  // Sort by relevance score descending
  return matches.sort(
    (a, b) => (b.score || b.relevance_score || 0) - (a.score || a.relevance_score || 0)
  );
}

/** Check if key fragments of a citation name appear in a title (e.g. "§ 14-9" in title). */
function _fragmentOverlap(citation: string, title: string): boolean {
  // Extract section markers like "§ 14-9" or "art. 5"
  const sectionPattern = /§\s*[\d-]+|art\.?\s*\d+|section\s*\d+/gi;
  const citSections = citation.match(sectionPattern) || [];
  const titleSections = title.match(sectionPattern) || [];
  if (citSections.length > 0 && titleSections.length > 0) {
    return citSections.some((cs) =>
      titleSections.some((ts) => cs.toLowerCase() === ts.toLowerCase())
    );
  }
  // Fall back to significant word overlap (3+ char words)
  const citWords = citation.split(/\s+/).filter((w) => w.length > 3);
  const titleWords = new Set(title.split(/\s+/).filter((w) => w.length > 3));
  const overlap = citWords.filter((w) => titleWords.has(w));
  return overlap.length >= 2;
}

/**
 * Find KeyAuthority info from synthesis data that matches the clicked citation.
 */
function findAuthorityInfo(
  citationName: string,
  synthesisData?: Record<string, any>
): KeyAuthority | null {
  if (!synthesisData) return null;

  const normalised = citationName.toLowerCase().trim();

  // Search in detailed_analysis sections' key_authorities
  const da = synthesisData.detailed_analysis;
  if (da && typeof da === 'object') {
    for (const section of Object.values(da) as any[]) {
      if (!section?.key_authorities) continue;
      for (const auth of section.key_authorities) {
        if (auth.citation?.toLowerCase().trim() === normalised) {
          return auth;
        }
      }
    }
  }

  // Also search in authority_integration
  const ai = synthesisData.authority_integration;
  if (ai) {
    for (const group of ['binding_authorities', 'persuasive_authorities']) {
      const citations = ai[group]?.citations || [];
      for (const c of citations) {
        if (typeof c === 'string' && c.toLowerCase().trim() === normalised) {
          return {
            citation: c,
            principle: ai[group]?.summary || '',
            weight: group === 'binding_authorities' ? 'binding' : 'persuasive',
          };
        }
      }
    }
  }

  return null;
}

const weightBadge: Record<string, string> = {
  binding: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  persuasive: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300',
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const ResearchCitationPanel: React.FC<ResearchCitationPanelProps> = ({
  citationName,
  synthesisData,
  retrievalData,
  onClose,
}) => {
  const { t } = useTranslation();

  const authority = useMemo(
    () => findAuthorityInfo(citationName, synthesisData),
    [citationName, synthesisData]
  );

  const matchingContent = useMemo(
    () => findMatchingRetrievalContent(citationName, retrievalData),
    [citationName, retrievalData]
  );

  return (
    <div className="w-full h-full bg-white dark:bg-gray-900 shadow-lg flex flex-col overflow-hidden">
      {/* ── Header ─────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <HiOutlineScale className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
          <h2 className="text-sm font-bold text-gray-900 dark:text-gray-100 truncate" style={{ fontFamily: "'Playfair Display', serif" }}>
            {citationName}
          </h2>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex-shrink-0"
        >
          <PiXLight className="w-4 h-4" />
        </button>
      </div>

      {/* ── Content ────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
        {/* Authority info (principle, weight, application notes) */}
        {authority && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <HiOutlineBookOpen className="w-4 h-4 text-indigo-500" />
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {t('Legal Principle')}
              </h3>
              {authority.weight && (
                <span
                  className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${
                    weightBadge[authority.weight] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                  }`}
                >
                  {authority.weight.charAt(0).toUpperCase() + authority.weight.slice(1)}
                </span>
              )}
            </div>
            <p className="text-sm leading-relaxed text-gray-800 dark:text-gray-200">
              {authority.principle}
            </p>
            {authority.application_notes && (
              <div className="p-2.5 bg-amber-50 dark:bg-amber-950/20 rounded-md border border-amber-100 dark:border-amber-900/30">
                <p className="text-xs text-amber-700 dark:text-amber-400 italic">
                  <span className="font-semibold not-italic">{t('Application')}: </span>
                  {authority.application_notes}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Matching retrieval content — the actual source text */}
        {matchingContent.length > 0 ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <HiOutlineDocumentText className="w-4 h-4 text-blue-500" />
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {t('Source Content')}
              </h3>
            </div>
            {matchingContent.map((cit, i) => (
              <div
                key={cit.id || i}
                className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
              >
                {/* Source title bar */}
                <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                    {cit.title || `${t('Source')} ${i + 1}`}
                  </span>
                  {(cit.score || cit.relevance_score) ? (
                    <span className="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0 ml-2">
                      {t('Relevance')}: {((cit.score || cit.relevance_score || 0) * 100).toFixed(0)}%
                    </span>
                  ) : null}
                </div>
                {/* Content text */}
                <div className="px-3 py-3">
                  <p className="text-sm leading-relaxed text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                    {cit.content || t('No content available.')}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* No matching retrieval content found */
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <HiOutlineMagnifyingGlass className="w-4 h-4 text-gray-400" />
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {t('Source Content')}
              </h3>
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
                {t('Source content for this citation is not available in the search results.')}
              </p>
              {!authority && (
                <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-2">
                  {t('This authority was referenced in the synthesis by the AI model.')}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Show all retrieval sources summary if no direct match but data exists */}
        {matchingContent.length === 0 && retrievalData && Object.keys(retrievalData).length > 0 && (
          <div className="space-y-3 mt-4">
            <div className="flex items-center gap-2">
              <HiOutlineDocumentText className="w-4 h-4 text-gray-400" />
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                {t('Available Sources')}
              </h3>
            </div>
            <div className="space-y-1.5">
              {Object.entries(retrievalData).map(([sourceKey, sourceData]: [string, any]) => {
                const citations: RetrievalCitation[] = sourceData?.citations || [];
                return (
                  <div key={sourceKey} className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-600 flex-shrink-0" />
                    <span className="font-medium">{sourceKey.replace(/_/g, ' ')}</span>
                    <span>— {citations.length} {t('documents')}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResearchCitationPanel;
